import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { onConsentChange } from "@/lib/consent";
import { captureAttribution, initAttributionConsentGate } from "@/lib/attribution";
import { syncMetaConsent, metaTrackPage } from "@/lib/metaPixel";

const TITLE_WAIT_MS = 1500;

// Single source of page_view: one for the current page when consented measurement starts, one per React route change
// (pathname or search; hash-only changes ignored), pushed once document.title reflects the new route.
export default function AnalyticsManager() {
  const { pathname, search } = useLocation();
  const routeKey = pathname + search;
  const prevTitle = useRef(typeof document !== "undefined" ? document.title : "");

  useEffect(() => {
    initAnalytics();
    // Paid-attribution capture, gated on advertising consent (see lib/attribution.js):
    // captures now if granted, re-captures on grant, purges stored data on withdrawal.
    const stopAttr = initAttributionConsentGate();
    // Meta Pixel: init only if advertising consent already granted (lib/metaPixel.js).
    syncMetaConsent();
    const stopConsent = onConsentChange((c) => {
      syncMetaConsent(); // grant → init pixel; revoke → stop sending new events
      if (c.analytics) {
        setTimeout(() => trackPageView(), 0);
        if (!window.location.pathname.startsWith("/admin")) {
          metaTrackPage(window.location.pathname, window.location.search); // first PageView on grant-after-landing
        }
      }
    });
    return () => { stopAttr(); stopConsent(); };
  }, []);

  useEffect(() => {
    captureAttribution(); // re-check on navigation (persists only when ad consent granted; never erases first/last)
    if (pathname.startsWith("/admin")) return undefined;
    metaTrackPage(pathname, search); // Meta PageView (+ViewContent on key landings) — gated + deduped
    const started = Date.now();
    const before = prevTitle.current;
    const timer = setInterval(() => {
      const title = document.title;
      const settled = title !== "" && title !== before;
      if (!settled && Date.now() - started < TITLE_WAIT_MS) return;
      clearInterval(timer);
      prevTitle.current = title;
      trackPageView();
    }, 100);
    return () => clearInterval(timer);
  }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
